import { useContext, useState } from 'react';
import { useIntl, defineMessages } from '@alltrails/shared/react-intl';
import { bool } from 'prop-types';
import Button from '@alltrails/shared/denali/components/Button';
import OutlinedTextField from '@alltrails/shared/components/OutlinedTextField';

import { PrintContext } from '../../printContext';
import { IMPERIAL_SIZES, METRIC_SIZES, CUSTOM, DIMENSIONS, PAPER_SIZE_LABELS } from '../../constants';
import LearnMore from '../LearnMore';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';

const PAPER_SIZE_STRINGS = defineMessages({
  PAPER_SIZE: { defaultMessage: 'Paper size' },
  CONTENT: {
    defaultMessage:
      'Choose the size of paper you would like your map printed out on. Dimensions of the different options can be found below. You can also create a custom sized map, if for example you were wanting to print a large poster sized map.'
  }
});

const MIN_VALUE = 5.5;
const MAX_VALUE = 16;
const ERROR_MESSAGE = `Min ${MIN_VALUE}", Max ${MAX_VALUE}"`;

const validDimension = dimension => dimension >= MIN_VALUE && dimension <= MAX_VALUE;

const PaperSize = ({ isMobileWidth }) => {
  const {
    formattedDefaultMessages: { PAPER_SIZE, CONTENT }
  } = useFormatMessage(PAPER_SIZE_STRINGS);
  const { metric, paperSize, setPaperSize, setPaperDimensionsWidth, setPaperDimensionsHeight, setPaperDimensionsValid } = useContext(PrintContext);
  const sizes = metric ? METRIC_SIZES : IMPERIAL_SIZES;
  const intl = useIntl();

  const [width, setWidth] = useState(DIMENSIONS[CUSTOM][0]);
  const [length, setLength] = useState(DIMENSIONS[CUSTOM][1]);
  const [widthError, setWidthError] = useState(false);
  const [lengthError, setLengthError] = useState(false);

  const handleSizeButtonClick = size => {
    if (size === CUSTOM) {
      setPaperDimensionsWidth(width);
      setPaperDimensionsHeight(length);
      setPaperDimensionsValid(validDimension(width) && validDimension(length));
    } else {
      setPaperDimensionsValid(true);
      setPaperDimensionsWidth(DIMENSIONS[size][0]);
      setPaperDimensionsHeight(DIMENSIONS[size][1]);
    }
    setPaperSize(size);
  };

  const handleChange = (side, value) => {
    let valueError = false;
    let numberValue = value;

    if (Number.isNaN(numberValue)) {
      numberValue = '';
    }

    if (!validDimension(numberValue)) {
      valueError = true;
    }

    if (side === 'width') {
      setWidthError(valueError);
      setWidth(numberValue);
      setPaperDimensionsWidth(numberValue);
      setPaperDimensionsHeight(length);
    } else {
      setLengthError(valueError);
      setLength(numberValue);
      setPaperDimensionsWidth(width);
      setPaperDimensionsHeight(numberValue);
    }

    setPaperDimensionsValid(!valueError);
  };

  const renderCustomInputs = () => {
    if (paperSize !== CUSTOM) {
      return null;
    }

    return (
      <div className="paperSizeInputs">
        <OutlinedTextField
          className="paperSize"
          label="Width"
          isError={widthError}
          errorMessage={widthError ? ERROR_MESSAGE : null}
          value={width?.toString()}
          changeHandler={event => handleChange('width', event.target.value)}
        />
        <OutlinedTextField
          className="paperSize"
          label="Length"
          isError={lengthError}
          errorMessage={lengthError ? ERROR_MESSAGE : null}
          value={length?.toString()}
          changeHandler={event => handleChange('length', event.target.value)}
        />
      </div>
    );
  };

  return (
    <div className="buttonRow">
      {sizes.map(size => (
        <Button
          key={`paper-size-${size}`}
          text={intl.formatMessage(PAPER_SIZE_LABELS[size])}
          className="settingsButton"
          onClick={() => handleSizeButtonClick(size)}
          testId={intl.formatMessage(PAPER_SIZE_LABELS[size])}
          variant={paperSize === size ? 'primary' : 'default'}
        />
      ))}
      {renderCustomInputs()}
      <LearnMore title={PAPER_SIZE} content={CONTENT} paperSize isMobileWidth={isMobileWidth} />
    </div>
  );
};

PaperSize.propTypes = {
  isMobileWidth: bool
};

PaperSize.defaultProps = {
  isMobileWidth: false
};

export { PaperSize };
